import { Alert, Button, Group, Modal, Select, Stack, TextInput } from '@mantine/core';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';

import { errorMessage } from '../api/client';
import { groupsApi } from '../api/studio';
import type { SaveStudentPayload, StudentResponse } from '../api/types';
import { ru } from '../i18n/ru';
import { formatMoney } from './format';
import { blankToNull, studentSchema, type StudentValues } from './schemas';

interface StudentFormModalProps {
  opened: boolean;
  student: StudentResponse | null;
  onClose: () => void;
  onSubmit: (payload: SaveStudentPayload) => Promise<void>;
}

/** Форма ученика для родителя. Стоимость здесь только показывается. */
export function StudentFormModal({ opened, student, onClose, onSubmit }: StudentFormModalProps) {
  const [failure, setFailure] = useState<string | null>(null);

  const groups = useQuery({
    queryKey: ['group-options'],
    queryFn: groupsApi.options,
    enabled: opened,
  });

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<StudentValues>({
    resolver: zodResolver(studentSchema),
    defaultValues: {
      lastName: student?.lastName ?? '',
      firstName: student?.firstName ?? '',
      middleName: student?.middleName ?? '',
      birthDate: student?.birthDate ?? '',
      groupId: student?.groupId ?? '',
    },
  });

  // useWatch, а не watch(): watch возвращает несохраняемую функцию,
  // из-за которой React Compiler отказывается оптимизировать компонент.
  const selectedGroupId = useWatch({ control, name: 'groupId' });
  const selectedGroup = groups.data?.find((g) => g.id === selectedGroupId);

  const submit = handleSubmit(async (values) => {
    setFailure(null);
    try {
      await onSubmit({
        lastName: values.lastName.trim(),
        firstName: values.firstName.trim(),
        middleName: blankToNull(values.middleName),
        birthDate: values.birthDate,
        groupId: values.groupId,
      });
      onClose();
    } catch (error) {
      setFailure(errorMessage(error));
    }
  });

  const noGroups = groups.isSuccess && groups.data.length === 0;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={student === null ? ru.students.add : ru.students.edit}
      centered
    >
      <form onSubmit={(event) => void submit(event)} noValidate>
        <Stack gap="md">
          {failure !== null && (
            <Alert color="red" title={ru.common.error}>
              {failure}
            </Alert>
          )}

          {noGroups && <Alert color="yellow">{ru.students.noGroups}</Alert>}

          <TextInput
            label={ru.fields.lastName}
            error={errors.lastName?.message}
            {...register('lastName')}
          />
          <TextInput
            label={ru.fields.firstName}
            error={errors.firstName?.message}
            {...register('firstName')}
          />
          <TextInput
            label={ru.fields.middleName + ru.fields.optional}
            error={errors.middleName?.message}
            {...register('middleName')}
          />
          <TextInput
            label={ru.students.birthDate}
            type="date"
            error={errors.birthDate?.message}
            {...register('birthDate')}
          />

          <Controller
            control={control}
            name="groupId"
            render={({ field }) => (
              <Select
                label={ru.students.group}
                placeholder={ru.students.group}
                data={(groups.data ?? []).map((g) => ({ value: g.id, label: g.name }))}
                value={field.value}
                onChange={(value) => field.onChange(value ?? '')}
                error={errors.groupId?.message}
                searchable
                nothingFoundMessage={ru.students.noGroups}
                {...(selectedGroup !== undefined
                  ? { description: `${ru.students.price}: ${formatMoney(selectedGroup.monthlyPrice)}` }
                  : {})}
              />
            )}
          />

          <Group justify="flex-end" mt="xs">
            <Button variant="default" onClick={onClose}>
              {ru.common.cancel}
            </Button>
            <Button type="submit" loading={isSubmitting} disabled={noGroups}>
              {ru.common.save}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
